/**
 * Normalize Arabic-Indic / Eastern Arabic (Kurdish) digits and related symbols to ASCII.
 * Same rules as BaseInput.vue (kept in sync for raw <input> fields outside BaseInput).
 */
export function normalizeToEnglishDigits(value) {
  if (value == null) return "";
  return String(value)
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 1632))
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 1776))
    .replace(/٫/g, ".")
    .replace(/[٬،,]/g, "")
    .replace(/−/g, "-");
}

/** Digits and at most one dot (decimal). */
export function sanitizeNumericInput(value) {
  const normalized = normalizeToEnglishDigits(value);
  const digitsAndDotsOnly = normalized.replace(/[^0-9.]/g, "");
  const firstDotIndex = digitsAndDotsOnly.indexOf(".");
  if (firstDotIndex === -1) return digitsAndDotsOnly;
  return (
    digitsAndDotsOnly.slice(0, firstDotIndex + 1) +
    digitsAndDotsOnly.slice(firstDotIndex + 1).replace(/\./g, "")
  );
}

/** Whole non-negative integers only (qty, bonus counts). */
export function sanitizeIntegerInput(value) {
  const normalized = normalizeToEnglishDigits(value);
  return normalized.replace(/[^0-9]/g, "");
}

/**
 * Preserve in-progress decimals (e.g. "52.", "9.0", ".04") while typing.
 * parseFloat alone drops trailing zeros ("9.0" → 9), which blocks typing "9.04".
 */
export function valueFromDecimalInput(next) {
  if (next === "") return "";
  const s = String(next);
  if (s === ".") return ".";
  if (s.endsWith(".")) return s;

  const n = parseFloat(s);
  if (!Number.isFinite(n)) return "";

  if (s.includes(".") && String(n) !== s) {
    return s;
  }

  return n;
}

/** Coerce a line decimal field to a number for math/API (incomplete "." uses fallback). */
export function numericFromDecimalField(value, fallback = 0) {
  if (value === "" || value === null || value === undefined || value === ".") {
    return fallback;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/** Bound input :value for cart/root decimal fields. */
export function lineDecimalInputDisplay(value) {
  if (value === "" || value === null || value === undefined) return "";
  return String(value);
}

/** Keep the native input value aligned with stored state (preserves "1." while typing). */
export function syncDecimalInputElement(e, storedValue) {
  if (!e?.target) return;
  const display = lineDecimalInputDisplay(storedValue);
  if (e.target.value !== display) {
    e.target.value = display;
  }
}

/**
 * Sanitize event input, convert for storage, and sync DOM before Vue re-render.
 * Use for all controlled decimal text inputs.
 */
export function processDecimalInputEvent(e, emptyValue = "") {
  const next = sanitizeNumericInput(e.target.value);
  let stored = emptyValue;
  if (next !== "") {
    stored = next === "." ? "." : valueFromDecimalInput(next);
  }
  syncDecimalInputElement(e, stored);
  return stored;
}

/** Cart line price/cost/discount from sanitized input string. */
export function applyLineDecimalInput(next, { emptyAs = "" } = {}) {
  if (next === "" || next === ".") return next === "." ? "." : emptyAs;
  return valueFromDecimalInput(next);
}

/** Root-level paid/discount fields on invoice forms. */
export function applyRootDecimalLocaleInput(next, emptyValue = 0) {
  if (next === "") return emptyValue;
  if (next === ".") return ".";
  return valueFromDecimalInput(next);
}

/** Percentage discount field (0–100), preserves trailing dot/zeros while typing. */
export function applyPercentageDecimalInput(next) {
  if (next === "" || next === ".") return next === "." ? "." : 0;
  const v = valueFromDecimalInput(next);
  if (typeof v === "string") return v;
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, n));
}
